<script setup lang="ts">
import CarrefourIcon from '#assets/svg/carrefour.svg?component';
import HomeIcon from '#assets/svg/home.svg?component';
import { useLanguage } from '#composables/useLanguage';
import { formatIndicatorTitle, formatIndicatorValue, getComparisonClass } from '#helpers';
import type { IndicatorBreakdown } from '@carrefour-gcs/shared';

defineProps<{
  breakdown: IndicatorBreakdown;
  isHomeIcon?: boolean;
}>();

const { t, language } = useLanguage();
</script>

<template>
  <div class="grid grid-cols-[auto_repeat(3,min-content)] gap-y-3 gap-x-2 truncate w-full text-sm items-center">
    <template v-for="{ isDivider, name, benchmark, comparison, value } in breakdown.values" :key="name">
      <!-- Divider -->
      <hr v-if="isDivider" class="col-start-1 col-span-full border-default-very-light" />

      <!-- Content -->
      <template v-else>
        <div v-if="name" class="col-start-1 col-span-1" v-text="t(name)" />
        <div
          v-if="value"
          class="col-start-2 col-span-1 font-bold"
          :class="{ 'text-primary text-xl': !breakdown.withTotal }"
          :title="formatIndicatorTitle(value)"
          v-text="formatIndicatorValue({ indicator: value, language })"
        />
        <div v-if="comparison">
          <div
            v-if="comparison"
            class="col-start-3 col-span-1 font-bold border-l pl-2"
            :class="getComparisonClass(comparison)"
            :title="formatIndicatorTitle(comparison)"
            v-text="formatIndicatorValue({ indicator: comparison, withPlusSign: true, language })"
          />
        </div>
        <div
          v-if="benchmark"
          class="col-span-1 font-bold border-l pl-2 flex space-x-1 items-center"
          :class="getComparisonClass(benchmark)"
        >
          <HomeIcon v-if="isHomeIcon" class="text-neutral-blueGray fill-neutral-blueGray w-5" />
          <CarrefourIcon v-else class="text-neutral-blueGray fill-neutral-blueGray w-5" />
          <span
            class="font-bold text-neutral-blueGray fill-neutral-blueGray"
            :title="formatIndicatorTitle(benchmark)"
            v-text="formatIndicatorValue({ indicator: benchmark, withPlusSign: true, language })"
          />
        </div>
      </template>
    </template>
  </div>
</template>
